import React from 'react';
import { Card } from 'react-bootstrap';
import { toast } from 'react-toastify';
import InlineCitation from './InlineCitation';
import { StylishNewButton } from '../DesignSystems/New/StylishNewButton';
import { SharedIcon } from '../SharedIcon/SharedIcon';
import { getEntityTypePluralLabel } from './incidentEntityComponents';
import { cleanDisplayName } from './citationUtils';
import { getBaseUrlFromLink } from '../../utils/urlHelpers';

interface SourceAnnotation {
  sourceId: string;
  sourceType: string;
  metadata: any;
  originalContent?: string;
}

/** Individual citation with its associated source */
interface CitationInfo {
  number: number;
  source: SourceAnnotation;
}

interface UniversalCitationCardProps {
  sourceType: string;
  /** Display name - for incidents this is the incident name */
  sourceName: string;
  /** Individual citations with their metadata for interactive rendering */
  citations: CitationInfo[];
  /** Handler for file citations */
  onFileCitationClick?: (fileId: string, page?: number) => void;
  /** Handler for incident citations */
  onIncidentCitationClick?: (metadata: any) => void;
  /** Handler for exercise citations */
  onExerciseCitationClick?: (metadata: any) => void;
  /** Handler for SITREP citations */
  onSitrepCitationClick?: () => void;
  /** Optional: scroll the chat back to the message that produced this citation */
  onScrollToSource?: () => void;
}

export default function UniversalCitationCard({
  sourceType,
  sourceName,
  citations,
  onFileCitationClick,
  onIncidentCitationClick,
  onExerciseCitationClick,
  onSitrepCitationClick,
  onScrollToSource,
}: UniversalCitationCardProps) {
  // Clean and truncate source names for display
  const cleanedName = cleanDisplayName(sourceName, sourceType);
  const displayName =
    cleanedName.length > 60
      ? `${cleanedName.substring(0, 57)}...`
      : cleanedName;

  // Sort citations by number
  const sortedCitations = [...citations].sort((a, b) => a.number - b.number);

  // Create click handler for each citation based on source type
  const getCitationClickHandler = (citation: CitationInfo) => {
    const { source } = citation;
    const { metadata } = source;

    switch (source.sourceType) {
      case 'FILES_RAG':
        return () => onFileCitationClick?.(metadata.fileId, metadata.page);
      case 'INCIDENT':
        return () => onIncidentCitationClick?.(metadata);
      case 'EXERCISE':
        return () => onExerciseCitationClick?.(metadata);
      case 'WEB':
        return () => {
          if (metadata.url) {
            window.open(metadata.url, '_blank', 'noopener,noreferrer');
          }
        };
      case 'SITREP':
        return () => onSitrepCitationClick?.();
      case 'CUSTOM':
        return () => {
          toast.info(
            `Source: ${metadata.sourceName || metadata.customSourceName || 'Custom Data'}`
          );
        };
      default:
        return () => {
          // For other sources, no-op
        };
    }
  };

  // Get category label for display (right side)
  const getCategoryText = () => {
    const firstSource = sortedCitations[0]?.source;
    const metadata = firstSource?.metadata || {};

    switch (sourceType) {
      case 'INCIDENT':
        return getEntityTypePluralLabel(metadata.entityType || '');
      case 'FILES_RAG': {
        const page = metadata.page;
        return page ? `p. ${page}` : null;
      }
      case 'WEB': {
        // Show base URL (e.g., "nytimes.com") for web sources
        return getBaseUrlFromLink(metadata.url) || null;
      }
      default:
        return null;
    }
  };

  const categoryText = getCategoryText();

  return (
    <Card className="universal-citation-card mb-2">
      <Card.Body className="py-2 px-3">
        <div className="d-flex flex-column gap-1">
          {/* Source header at top - three distinct elements */}
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center gap-2">
              {/* Source type badge */}
              <span
                className="badge bg-primary"
                style={{ fontSize: '0.7rem', fontWeight: 600 }}
              >
                {sourceType === 'FILES_RAG' ? 'FILE' : sourceType}
              </span>
              {/* Source name - prominent */}
              <span className="fw-bold">{displayName}</span>
              {onScrollToSource && (
                <StylishNewButton
                  className="button--secondary button--icon"
                  onClick={onScrollToSource}
                  title="Jump to chat message"
                >
                  <SharedIcon iconName="arrow_upward" size="sm" />
                </StylishNewButton>
              )}
            </div>
            {/* Category - right aligned, white italic text */}
            {categoryText && (
              <span
                className="fst-italic text-white"
                style={{ fontSize: '0.9rem' }}
              >
                {categoryText}
              </span>
            )}
          </div>
          {/* Citations below */}
          <div className="citation-numbers">
            {sortedCitations.map((citation, index) => (
              <React.Fragment key={citation.number}>
                {index > 0 && ', '}
                <InlineCitation
                  citationNumber={citation.number}
                  fileName={citation.source.metadata.sourceName || sourceName}
                  sourceType={sourceType}
                  page={citation.source.metadata.page}
                  incidentName={citation.source.metadata.incidentName}
                  entityType={citation.source.metadata.entityType}
                  baseUrl={
                    sourceType === 'WEB'
                      ? getBaseUrlFromLink(citation.source.metadata.url) ||
                        undefined
                      : undefined
                  }
                  onClick={getCitationClickHandler(citation)}
                />
              </React.Fragment>
            ))}
          </div>
        </div>
      </Card.Body>
    </Card>
  );
}
